"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { getGalleryHomeItems } from "@/data/gallery";
import { useLanguage, useTranslations } from "@/lib/i18n";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
};

export default function Gallery() {
  const { language } = useLanguage();
  const t = useTranslations();
  const galleryItems = getGalleryHomeItems(language);

  return (
    <section id="gallery" className="py-24 md:py-32 bg-[#F9F9F9]">
      <div className="container mx-auto px-6 md:px-12">
        {/* HEADER SECTION */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-4">
              {t.gallery.tagline}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mb-4 text-brand-charcoal">
              {t.gallery.title}
            </h2>
            <p className="text-gray-500 font-light max-w-lg leading-relaxed">
              {t.gallery.subtitle}
            </p>
          </motion.div>

          {/* BUTTON DESKTOP */}
          <div className="hidden md:block">
            <Link href="/gallery">
              <Button
                variant="outline"
                className="rounded-none border-brand-charcoal text-brand-charcoal hover:bg-brand-charcoal hover:text-white px-8 py-6 uppercase text-xs font-bold tracking-[0.2em] transition-all"
              >
                {t.gallery.exploreAll}
              </Button>
            </Link>
          </div>
        </div>

        {/* BENTO GRID GALLERY */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-4 md:auto-rows-[280px] gap-4 md:gap-6"
        >
          {galleryItems.map((item) => (
            <motion.div
              key={item.id}
              variants={itemVariants}
              className={cn(
                "relative overflow-hidden group cursor-pointer w-full h-full bg-gray-200",
                item.className,
              )}
            >
              <Image
                src={item.src}
                alt={item.alt}
                fill
                className="object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />

              {/* Overlay Text */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center p-6">
                <span className="text-white font-serif text-lg tracking-wider text-center translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-100">
                  {item.alt}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* BUTTON MOBILE */}
        <div className="mt-8 md:hidden text-center">
          <Link href="/gallery">
            <Button
              variant="outline"
              className="w-full rounded-none border-brand-charcoal text-brand-charcoal hover:bg-brand-charcoal hover:text-white py-6 uppercase text-xs font-bold tracking-[0.2em]"
            >
              {t.gallery.exploreAll}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
